/**
 * URL validation patterns for media import.
 * Cobalt-supported platforms are fetched dynamically from the server.
 */

const BLOCKED_HOST_PATTERNS = [
  /^localhost$/i,
  /^127\./,
  /^10\./,
  /^172\.(1[6-9]|2\d|3[01])\./,
  /^192\.168\./,
  /^169\.254\./,
  /^0\./,
  /^::1$/, // IPv6 loopback
  /^::ffff:127\./, // IPv4-mapped loopback
  /^::ffff:10\./,
  /^::ffff:192\.168\./,
  /^::ffff:172\.(1[6-9]|2\d|3[01])\./,
  /^f[cd]/i,
  /^fe80/i,
];

export function isBlockedUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:")
      return true;
    if (parsed.username || parsed.password) return true;
    const host = parsed.hostname;
    if (!host) return true;
    return BLOCKED_HOST_PATTERNS.some((p) => p.test(host));
  } catch {
    return true;
  }
}

/** YouTube URL pattern */
export const YOUTUBE_PATTERN =
  /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11}).*$/;

/** Vimeo URL pattern */
export const VIMEO_PATTERN =
  /^(?:https?:\/\/)?(?:www\.|player\.)?vimeo\.com\/(?:video\/|channels\/[\w]+\/|groups\/[\w]+\/videos\/|album\/\d+\/video\/)?(\d+)(?:\/[\w]+)?(?:\?.*)?$/;

/** Direct audio file URL pattern */
export const AUDIO_URL_PATTERN =
  /(http(s?):)([/|.|\w|\s|-])*\.(?:mp3|wav|ogg|webm|m4a|flac|m4p|aac|mpeg|amr)/;

/** Direct video file URL pattern */
export const VIDEO_URL_PATTERN =
  /(http(s?):)([/|.|\w|\s|-])*\.(?:mp4|wmv|avi|m4v|mov|flv)/;

/**
 * Supported import platform hostnames.
 * Maps primary hostnames to display name and icon identifier.
 *
 * Everything here except YouTube resolves through Cobalt. YouTube takes its own path: the server
 * resolves it separately and downloads via a Cloudflare Worker, because googlevideo answers 403 to
 * our AWS egress. That path is gated server-side, so this list should only advertise YouTube while
 * `YOUTUBE_IMPORT_ENABLED` is true.
 */
export const COBALT_PLATFORM_MAP: Record<
  string,
  { name: string; icon: string }
> = {
  "youtube.com": { name: "YouTube", icon: "youtube" },
  "tiktok.com": { name: "TikTok", icon: "tiktok" },
  "instagram.com": { name: "Instagram", icon: "instagram" },
  "twitter.com": { name: "X", icon: "x-twitter" },
  "x.com": { name: "X", icon: "x-twitter" },
  "reddit.com": { name: "Reddit", icon: "reddit" },
  "soundcloud.com": { name: "SoundCloud", icon: "soundcloud" },
  "facebook.com": { name: "Facebook", icon: "facebook" },
  "vimeo.com": { name: "Vimeo", icon: "vimeo" },
  "snapchat.com": { name: "Snapchat", icon: "snapchat" },
  "vk.com": { name: "VK", icon: "vk" },
};

/**
 * Build a regex pattern from a list of Cobalt-supported hostnames.
 * Returns null if the list is empty.
 */
export function buildCobaltPattern(hosts: string[]): RegExp | null {
  if (hosts.length === 0) return null;
  const escaped = hosts.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return new RegExp(`^https?:\\/\\/(www\\.)?(${escaped.join("|")})\\/`, "i");
}

/**
 * Check if a URL matches a Cobalt-supported platform.
 */
export function isCobaltUrl(url: string, pattern: RegExp | null): boolean {
  if (!pattern) return false;
  return pattern.test(url);
}

/**
 * Detect the type of media URL.
 */
export function detectUrlMediaType(
  url: string,
  cobaltPattern: RegExp | null,
): "audio" | "video" | null {
  if (AUDIO_URL_PATTERN.test(url)) return "audio";
  if (VIDEO_URL_PATTERN.test(url)) return "video";
  if (YOUTUBE_PATTERN.test(url)) return "video";
  if (VIMEO_PATTERN.test(url)) return "video";
  if (isCobaltUrl(url, cobaltPattern)) return "video";
  return null;
}

/** Platform behind a pasted URL, for the row icon. Host-based so `evil.com/?x=youtube.com` cannot spoof it. */
export function detectPlatform(
  url: string,
): { name: string; icon: string } | null {
  if (!url) return null;
  try {
    const trimmed = url.trim();
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(withScheme);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;

    const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
    for (const [platformHost, meta] of Object.entries(COBALT_PLATFORM_MAP)) {
      if (host === platformHost || host.endsWith(`.${platformHost}`)) return meta;
    }
    // Short links sit outside the map, which is keyed by canonical host.
    if (host === "youtu.be") return COBALT_PLATFORM_MAP["youtube.com"];
    return null;
  } catch {
    return null;
  }
}

/**
 * Build platform icon list from server-provided hostnames.
 */
export function buildPlatformIcons(
  hosts: string[],
): Array<{ name: string; icon: string }> {
  const seen = new Set<string>();
  const icons: Array<{ name: string; icon: string }> = [];

  for (const host of hosts) {
    const mapped = COBALT_PLATFORM_MAP[host];
    if (mapped && !seen.has(mapped.icon)) {
      seen.add(mapped.icon);
      icons.push(mapped);
    }
  }

  // YouTube leads rather than sitting wherever the server lists it.
  return icons.sort((a, b) =>
    a.icon === "youtube" ? -1 : b.icon === "youtube" ? 1 : 0,
  );
}
